/**
 * The tube a tunnel runs in, and the portals where it enters the ground.
 *
 * A tunnel's road is an ordinary ribbon, laid by `solveTunnel` on a floor
 * straight from portal to portal under the terrain, which is left whole over
 * it. Around the road this builds:
 *
 * - **The tube**: a walkway either side of the road, walls up to a chamfer
 *   and a flat ceiling, and a lamp every `LAMP_SPACING` metres. Drawn unlit,
 *   as the sun does not reach in; the lamps are its light.
 * - **The portals**, at each end that is not cut off by the tile's edge: a
 *   headwall round the opening, up past the ground over it, and a wing wall
 *   either side, out along the approach and down with the ground, over a
 *   slab between them. The terrain inside the walls is not drawn (see
 *   `Portal` and `terrainMaterial.ts`), so the road runs in between them to
 *   the opening instead of under the hillside's first triangles.
 * - **The floor**, road and walkways, to stand and drive on, and to tell
 *   that a point is inside a tunnel, where the ground over it is not the
 *   ground (see `ThreeRenderer.ground`).
 *
 * Plain arrays in chunk-local metres, like the bridge structure.
 */

import { TUNNEL } from './roads.js';
import { ROAD_STYLE } from '../sources/shortbread.js';
import { TUNNEL_ROOF, tunnelHeadroom } from './tunnels.js';
import { WALL_HALF } from './colliders.js';

/** Walkway either side of the road inside the tube, metres. */
const WALKWAY = 1.0;
/** Between lamps along the ceiling, metres. */
const LAMP_SPACING = 25;
/** How far out from the headwall the wing walls run, metres. */
const WING = 10;
/** Pieces a wing wall's top is laid in, following the ground beside it, metres. */
const WING_PIECE = 2;
/** How far out past a wing wall, in its thicknesses, the ground its top follows is taken. */
const WING_BANK = 3;
/** Linear RGB, unlit: lamp-lit concrete and the lamps. */
const WALL = [0.2, 0.2, 0.19];
const CEILING = [0.09, 0.09, 0.09];
const WALKWAY_TONE = [0.12, 0.12, 0.12];
const LAMP = [1.6, 1.35, 0.9];
/** Linear RGB, lit: the portal's concrete, as a bridge's. */
const CONCRETE = [0.5, 0.48, 0.44];

/**
 * A portal's mouth, where the terrain is cut away: from the headwall at
 * (x, z) out along (dx, dz), `length` metres, `halfWidth` either side of the
 * centreline, everything over `floor`; and back in from the headwall,
 * `PORTAL_INSIDE` metres, between the floor and `ceiling`, where the hill
 * over the tube is shallower than the tube is tall. Chunk-local.
 */
export interface Portal {
  x: number; z: number;
  dx: number; dz: number;
  halfWidth: number;
  /** Half the tube's width, to its walls, where the terrain inside it is cut away; by default `halfWidth`. */
  tubeHalfWidth?: number;
  length: number;
  floor: number;
  ceiling: number;
  top: number;
}

/** How far into the tunnel from a portal the terrain inside the tube is cut away, metres. */
export const PORTAL_INSIDE = 40;

export interface TunnelMeshes {
  /** The tube, x, y, z triples, linear RGB per vertex, unlit. */
  inside: { positions: Float32Array; colors: Float32Array; indices: Uint32Array };
  /** Portal walls and slabs, lit, drawn with the bridge structure. */
  portals: { positions: Float32Array; colors: Float32Array; indices: Uint32Array };
  /** Floor across the whole tube, road and walkways, to stand on; `roof` the same, at the ceiling. */
  floor: { positions: Float32Array; indices: Uint32Array; roof: Float32Array };
  mouths: Portal[];
  /** The tube's walls and the portals', solid: x0, z0, x1, z1, bottom, top per wall. See `buildColliders`. */
  walls: Float32Array;
  /** What is in a tube or a portal's mouth. */
  volume: TunnelVolume;
}

interface Section { lx: number; ly: number; lz: number; rx: number; ry: number; rz: number }

/** Interval of a line inside a convex tunnel cross-section footprint. */
function segmentInPolygon(ax: number, az: number, bx: number, bz: number, poly: Array<[number, number]>): [number, number] | undefined {
  const cx = poly.reduce((s, p) => s + p[0], 0) / poly.length, cz = poly.reduce((s, p) => s + p[1], 0) / poly.length;
  let lo = 0, hi = 1;
  for (let i = 0; i < poly.length; i++) {
    const [x, z] = poly[i], [ex, ez] = poly[(i + 1) % poly.length];
    const nx = z - ez, nz = ex - x, sign = nx * (cx - x) + nz * (cz - z) >= 0 ? 1 : -1;
    const a = (nx * (ax - x) + nz * (az - z)) * sign;
    const b = (nx * (bx - x) + nz * (bz - z)) * sign;
    if (a < 0 && b < 0) return undefined;
    if ((a < 0) !== (b < 0)) {
      const t = a / (a - b);
      if (a < 0) lo = Math.max(lo, t); else hi = Math.min(hi, t);
    }
    if (lo >= hi) return undefined;
  }
  return [lo, hi];
}

type Mesh = { positions: number[]; colors: number[]; indices: number[] };

export function buildTunnelMeshes(
  positions: Float32Array, indices: Uint32Array, attributes: Float32Array, bridges: Uint8Array,
  ground: (x: number, z: number) => number, width: number, depth: number,
): TunnelMeshes {
  const inside: Mesh = { positions: [], colors: [], indices: [] };
  const portals: Mesh = { positions: [], colors: [], indices: [] };
  const floor = { positions: [] as number[], indices: [] as number[], roof: [] as number[] };
  const mouths: Portal[] = [];
  const walls: number[] = [];
  const volume = new TunnelVolume();
  const quad = (m: Mesh, a: number[], b: number[], c: number[], d: number[], col: number[]) => {
    const base = m.positions.length / 3;
    m.positions.push(...a, ...b, ...c, ...d);
    for (let k = 0; k < 4; k++) m.colors.push(col[0], col[1], col[2]);
    m.indices.push(base, base + 1, base + 2, base, base + 2, base + 3);
  };

  const runs = tunnelRuns(indices, bridges);
  const surfaceEnds = surfaceRunEnds(indices, bridges, v => [(positions[v * 3] + positions[(v + 1) * 3]) / 2, (positions[v * 3 + 2] + positions[(v + 1) * 3 + 2]) / 2]);
  const runsOn = tunnelJoins(runs, (v) => [(positions[v * 3] + positions[(v + 1) * 3]) / 2, (positions[v * 3 + 2] + positions[(v + 1) * 3 + 2]) / 2]);
  const allSections = runs.map(run => {
    const style = Math.round(attributes[run[0] * 4 + 2]);
    const walk = style === ROAD_STYLE.rail ? WALKWAY + 0.3 : style > ROAD_STYLE.service ? 0.3 : WALKWAY;
    // Each cross-section widened by the walkways, at the road's level.
    return run.map(a => {
      const lx = positions[a * 3], lz = positions[a * 3 + 2], rx = positions[(a + 1) * 3], rz = positions[(a + 1) * 3 + 2];
      const nx = rx - lx, nz = rz - lz, len = Math.hypot(nx, nz) || 1;
      const y = Math.min(positions[a * 3 + 1], positions[(a + 1) * 3 + 1]) - 0.03;
      return { lx: lx - nx / len * walk, ly: y, lz: lz - nz / len * walk, rx: rx + nx / len * walk, ry: y, rz: rz + nz / len * walk };
    });
  });
  // Side walls are boundaries of the combined tunnel space. A separate
  // mapped branch or overlapping carriageway must not wall off its neighbour.
  const rooms = allSections.flatMap((sections, ri) => sections.slice(1).map((b, i) => {
    const a = sections[i];
    return { ri, segment: i, a, b, height: tunnelHeadroom(Math.round(attributes[runs[ri][0] * 4 + 2])),
      outline: [[a.lx, a.lz], [a.rx, a.rz], [b.rx, b.rz], [b.lx, b.lz]] as Array<[number, number]> };
  }));
  // Include the open approaches in the union: a neighbouring portal's wing
  // or headwall must not run across another bore's entrance.
  for (const [ri, sections] of allSections.entries()) for (const end of [0, sections.length - 1]) {
    if (runsOn.has(ri * 2 + (end === 0 ? 0 : 1))) continue;
    const a = sections[end], inner = sections[end === 0 ? 1 : sections.length - 2];
    const x = (a.lx + a.rx) / 2, z = (a.lz + a.rz) / 2;
    if (!inner || !surfaceEnds.some(([sx, sz]) => Math.hypot(sx - x, sz - z) < 1.5)) continue;
    const dx = x - (inner.lx + inner.rx) / 2, dz = z - (inner.lz + inner.rz) / 2, len = Math.hypot(dx, dz) || 1;
    const b = { ...a, lx: a.lx + dx / len * WING, rx: a.rx + dx / len * WING,
      lz: a.lz + dz / len * WING, rz: a.rz + dz / len * WING };
    rooms.push({ ri, segment: -1, a, b, height: tunnelHeadroom(Math.round(attributes[runs[ri][0] * 4 + 2])),
      outline: [[a.lx, a.lz], [a.rx, a.rz], [b.rx, b.rz], [b.lx, b.lz]] });
  }
  for (const [ri, run] of runs.entries()) {
    const style = Math.round(attributes[run[0] * 4 + 2]);
    const headroom = tunnelHeadroom(style);
    const walk = style === ROAD_STYLE.rail ? WALKWAY + 0.3 : style > ROAD_STYLE.service ? 0.3 : WALKWAY;
    const sections = allSections[ri];
    let travelled = 0, nextLamp = LAMP_SPACING / 2;
    for (let i = 0; i + 1 < sections.length; i++) {
      const a = sections[i], b = sections[i + 1];
      const ring = (s: Section) => {
        const ix = (s.rx - s.lx), iz = (s.rz - s.lz), w = Math.hypot(ix, iz) || 1;
        const cham = Math.min(1.2, w * 0.2);
        return [
          [s.lx, s.ly, s.lz],
          [s.lx, s.ly + headroom - cham, s.lz],
          [s.lx + ix / w * cham, s.ly + headroom, s.lz + iz / w * cham],
          [s.rx - ix / w * cham, s.ry + headroom, s.rz - iz / w * cham],
          [s.rx, s.ry + headroom - cham, s.rz],
          [s.rx, s.ry, s.rz],
        ];
      };
      const ra = ring(a), rb = ring(b);
      const tones = [WALL, CEILING, CEILING, CEILING, WALL];
      for (let k = 1; k < 4; k++) quad(inside, ra[k], rb[k], rb[k + 1], ra[k + 1], tones[k]);
      // The walkways, just over the floor, and the floor to stand on.
      quad(inside, ra[0], ra[5], rb[5], rb[0], WALKWAY_TONE);
      // Solid, from under the floor to the roof, and the tube's room to keep clear.
      const roofTop = a.ly + headroom + TUNNEL_ROOF;
      for (const side of [0, 5]) {
        const p = ra[side], q = rb[side], upper = side === 0 ? 1 : 4;
        // The drawn wall is its inside face. Put the collider's thickness
        // behind that face, not 30 cm into the usable tunnel on either side.
        const len = Math.hypot(q[0] - p[0], q[2] - p[2]) || 1;
        let nx = -(q[2] - p[2]) / len, nz = (q[0] - p[0]) / len;
        const cx = (a.lx + a.rx + b.lx + b.rx) / 4, cz = (a.lz + a.rz + b.lz + b.rz) / 4;
        if ((cx - p[0]) * nx + (cz - p[2]) * nz > 0) { nx = -nx; nz = -nz; }
        const openings: Array<{ lo: number; hi: number; top: number }> = [];
        for (const room of rooms) {
          // A tight bend can overlap its own earlier sections too. Only
          // this wall's own section is excluded from the union.
          if (room.ri === ri && room.segment === i) continue;
          const span = segmentInPolygon(p[0], p[2], q[0], q[2], room.outline);
          if (span === undefined) continue;
          const mid = (span[0] + span[1]) / 2, y = p[1] + (q[1] - p[1]) * mid;
          const x = p[0] + (q[0] - p[0]) * mid, z = p[2] + (q[2] - p[2]) * mid;
          const ax = (room.a.lx + room.a.rx) / 2, az = (room.a.lz + room.a.rz) / 2;
          const dx = (room.b.lx + room.b.rx) / 2 - ax, dz = (room.b.lz + room.b.rz) / 2 - az;
          const u = Math.max(0, Math.min(1, ((x - ax) * dx + (z - az) * dz) / (dx * dx + dz * dz || 1)));
          const otherFloor = room.a.ly + (room.b.ly - room.a.ly) * u;
          if (Math.abs(otherFloor - y) > 0.75) continue;
          openings.push({ lo: span[0], hi: span[1], top: otherFloor + room.height });
        }
        const stops = [...new Set([0, 1, ...openings.flatMap(o => [o.lo, o.hi])])].sort((x, y) => x - y);
        const mix = (x: number[], y: number[], t: number) => x.map((v, k) => v + (y[k] - v) * t);
        for (let k = 0; k + 1 < stops.length; k++) {
          const lo = stops[k], hi = stops[k + 1], mid = (lo + hi) / 2;
          if (hi - lo < 1e-6) continue;
          let bottom = -Infinity;
          for (const o of openings) if (mid > o.lo && mid < o.hi) bottom = Math.max(bottom, o.top);
          const p0 = mix(p, q, lo), p1 = mix(p, q, hi), top0 = mix(ra[upper], rb[upper], lo), top1 = mix(ra[upper], rb[upper], hi);
          p0[1] = Math.min(top0[1], Math.max(p0[1], bottom)); p1[1] = Math.min(top1[1], Math.max(p1[1], bottom));
          if (top0[1] > p0[1] + 1e-4 || top1[1] > p1[1] + 1e-4) {
            if (side === 0) quad(inside, p0, p1, top1, top0, WALL);
            else quad(inside, top0, top1, p1, p0, WALL);
          }
          const low = Math.max(Math.min(a.ly, b.ly) - 0.5, bottom);
          if (low < roofTop) walls.push(p0[0] + nx * WALL_HALF, p0[2] + nz * WALL_HALF,
            p1[0] + nx * WALL_HALF, p1[2] + nz * WALL_HALF, low, roofTop);
        }
      }
      volume.add([[ra[0][0], ra[0][2]], [ra[5][0], ra[5][2]], [rb[5][0], rb[5][2]], [rb[0][0], rb[0][2]]], Math.min(a.ly, b.ly), Math.max(a.ly, b.ly) + headroom + TUNNEL_ROOF);
      const fb = floor.positions.length / 3;
      floor.positions.push(...ra[0], ...ra[5], ...rb[0], ...rb[5]);
      for (const p of [ra[0], ra[5], rb[0], rb[5]]) floor.roof.push(p[0], p[1] + headroom, p[2]);
      floor.indices.push(fb, fb + 2, fb + 1, fb + 1, fb + 2, fb + 3);
      // Lamps down the middle of the ceiling.
      const step = Math.hypot((b.lx + b.rx - a.lx - a.rx) / 2, (b.lz + b.rz - a.lz - a.rz) / 2);
      while (nextLamp < travelled + step) {
        const t = (nextLamp - travelled) / Math.max(1e-6, step);
        const mix = (u: number[], v: number[]) => u.map((c, j) => c + (v[j] - c) * t);
        const c2 = mix(ra[2], rb[2]), c3 = mix(ra[3], rb[3]);
        const cx = (c2[0] + c3[0]) / 2, cy = c2[1] - 0.05, cz = (c2[2] + c3[2]) / 2;
        const ax = (rb[2][0] - ra[2][0]) / Math.max(1e-6, step), az = (rb[2][2] - ra[2][2]) / Math.max(1e-6, step);
        const sx = -az * 0.35, sz = ax * 0.35;
        quad(inside, [cx - ax - sx, cy, cz - az - sz], [cx + ax - sx, cy, cz + az - sz], [cx + ax + sx, cy, cz + az + sz], [cx - ax + sx, cy, cz - az + sz], LAMP);
        nextLamp += LAMP_SPACING;
      }
      travelled += step;
    }

    // Portals, where the tunnel ends inside the tile: not where the tile cuts
    // it, nor where another way of the tunnel runs on.
    for (const end of [0, sections.length - 1]) {
      if (runsOn.has(ri * 2 + (end === 0 ? 0 : 1))) continue;
      const s = sections[end], o = sections[end === 0 ? 1 : sections.length - 2];
      if (o === undefined) continue;
      const cx = (s.lx + s.rx) / 2, cz = (s.lz + s.rz) / 2;
      if (cx < 0.5 || cz < 0.5 || cx > width - 0.5 || cz > depth - 0.5) continue;
      // A mapped underground dead end (garage, station or unfinished bore)
      // is not an entrance onto the city surface. Close the tube below
      // ground instead of cutting a false portal and a cliff to street level.
      if (!surfaceEnds.some(([x, z]) => Math.hypot(x - cx, z - cz) < 1.5)) {
        const top = s.ly + headroom;
        const cap = [[s.lx, s.ly, s.lz], [s.rx, s.ly, s.rz], [s.rx, top, s.rz], [s.lx, top, s.lz]];
        if (end !== 0) cap.reverse();
        quad(inside, cap[0], cap[1], cap[2], cap[3], WALL);
        walls.push(s.lx, s.lz, s.rx, s.rz, s.ly - 0.5, top);
        continue;
      }
      let dx = cx - (o.lx + o.rx) / 2, dz = cz - (o.lz + o.rz) / 2;
      const dl = Math.hypot(dx, dz) || 1;
      dx /= dl; dz /= dl;
      const half = Math.hypot(s.rx - s.lx, s.rz - s.lz) / 2;
      // Across, from the left of the opening to its right, square to the way out.
      const ax = -dz, az = dx;
      const side = (s.rx - s.lx) * ax + (s.rz - s.lz) * az < 0 ? -1 : 1;
      const px = ax * side, pz = az * side;
      const y = s.ly;
      // The ground over the opening and along the wing walls, to build up to.
      let over = y + headroom + 1;
      for (let back = 0; back <= 6; back += 2) {
        for (const off of [-half, 0, half]) over = Math.max(over, ground(cx - dx * back + px * off, cz - dz * back + pz * off) + 0.4);
      }
      // Out past the tube's walls, which the walkways set wider than the road.
      const outer = Math.max(half + 0.6, half + walk + 0.3);
      const at = (along: number, across: number, h: number) => [cx + dx * along + px * across, h, cz + dz * along + pz * across];
      const portalWall = (p: number[], q: number[], h0: number, h1: number) => {
        const openings: Array<{ lo: number; hi: number; top: number }> = [];
        for (const room of rooms) {
          if (room.ri === ri || Math.abs((room.a.ly + room.b.ly) / 2 - y) > 0.75) continue;
          const span = segmentInPolygon(p[0], p[2], q[0], q[2], room.outline);
          if (span) openings.push({ lo: span[0], hi: span[1], top: Math.min(room.a.ly, room.b.ly) + room.height });
        }
        const stops = [...new Set([0, 1, ...openings.flatMap(o => [o.lo, o.hi])])].sort((a, b) => a - b);
        for (let i = 0; i + 1 < stops.length; i++) {
          const lo = stops[i], hi = stops[i + 1], mid = (lo + hi) / 2;
          let bottom = y - 0.6;
          for (const o of openings) if (mid > o.lo && mid < o.hi) bottom = Math.max(bottom, o.top);
          const top0 = h0 + (h1 - h0) * lo, top1 = h0 + (h1 - h0) * hi;
          if (bottom >= Math.max(top0, top1) || hi - lo < 1e-6) continue;
          const x0 = p[0] + (q[0] - p[0]) * lo, z0 = p[2] + (q[2] - p[2]) * lo;
          const x1 = p[0] + (q[0] - p[0]) * hi, z1 = p[2] + (q[2] - p[2]) * hi;
          quad(portals, [x0, Math.min(bottom, top0), z0], [x1, Math.min(bottom, top1), z1], [x1, top1, z1], [x0, top0, z0], CONCRETE);
          walls.push(x0, z0, x1, z1, bottom, Math.max(top0, top1));
        }
      };
      // Headwall: either side of the opening and over it.
      portalWall(at(0, -outer, 0), at(0, -half, 0), over, over);
      portalWall(at(0, half, 0), at(0, outer, 0), over, over);
      quad(portals, at(0, -half, y + headroom), at(0, half, y + headroom), at(0, half, over), at(0, -half, over), CONCRETE);
      // Wing walls, out along the approach and down with the ground beside
      // them, and the slab between. Down to the ground straight ahead at their
      // far end, as they were, they stood up out of the banks of an
      // underpass's cutting, which fall away to its floor only further out.
      for (const across of [-half, half]) {
        const t = across < 0 ? -0.4 : 0.4;
        const topAt = (along: number) => along <= 0 ? over
          : Math.max(y + 0.9, Math.min(over, ground(cx + dx * along + px * (across + t * WING_BANK), cz + dz * along + pz * (across + t * WING_BANK)) + 0.3));
        for (let a0 = 0; a0 < WING - 1e-6; a0 += WING_PIECE) {
          const a1 = Math.min(WING, a0 + WING_PIECE), h0 = topAt(a0), h1 = topAt(a1);
          portalWall(at(a0, across, 0), at(a1, across, 0), h0, h1);
          // The wall's top, so it reads as a wall from above.
          quad(portals, at(a0, across, h0), at(a1, across, h1), at(a1, across + t, h1), at(a0, across + t, h0), CONCRETE);
        }
      }
      quad(portals, at(0, -half, y - 0.02), at(0, half, y - 0.02), at(WING, half, y - 0.02), at(WING, -half, y - 0.02), CONCRETE);
      mouths.push({ x: cx, z: cz, dx, dz, halfWidth: half, tubeHalfWidth: half + walk, length: WING, floor: y, ceiling: y + headroom + TUNNEL_ROOF, top: over });
    }
  }
  const pack = (m: Mesh) => ({ positions: new Float32Array(m.positions), colors: new Float32Array(m.colors), indices: new Uint32Array(m.indices) });
  return {
    inside: pack(inside),
    portals: pack(portals),
    floor: { positions: new Float32Array(floor.positions), indices: new Uint32Array(floor.indices), roof: new Float32Array(floor.roof) },
    mouths,
    walls: new Float32Array(walls),
    volume: volume.withMouths(mouths),
  };
}

/**
 * The room inside the tubes and the portals' mouths, to keep clear of what
 * stands on the land: a tree on the hill over a portal, its foot where the
 * terrain is not drawn, stood in the mouth; a house over a shallow tunnel
 * stood in the tube.
 */
export class TunnelVolume {
  private readonly pieces: Array<{ outline: Array<[number, number]>; bottom: number; top: number; box: number[] }> = [];
  private mouths: Portal[] = [];

  add(outline: Array<[number, number]>, bottom: number, top: number): void {
    const xs = outline.map(p => p[0]), zs = outline.map(p => p[1]);
    this.pieces.push({ outline, bottom, top, box: [Math.min(...xs), Math.min(...zs), Math.max(...xs), Math.max(...zs)] });
  }

  withMouths(mouths: Portal[]): this {
    this.mouths = [...this.mouths, ...mouths];
    return this;
  }

  /** Convex openings for cutting building faces, including open portal approaches. */
  openings(): Array<{ outline: Array<[number, number]>; bottom: number; top: number }> {
    return [...this.pieces, ...this.mouths.map(m => {
      const at = (along: number, across: number): [number, number] =>
        [m.x + m.dx * along - m.dz * across, m.z + m.dz * along + m.dx * across];
      return { outline: [at(0, -m.halfWidth), at(m.length, -m.halfWidth), at(m.length, m.halfWidth), at(0, m.halfWidth)],
        bottom: m.floor - 0.3, top: Infinity };
    })];
  }

  get empty(): boolean {
    return this.pieces.length === 0 && this.mouths.length === 0;
  }

  /**
   * Whether something standing at (x, z), from `bottom` to `top`, within
   * `pad` metres of it across, reaches into a tube or a portal's mouth.
   */
  reaches(x: number, z: number, bottom: number, top: number, pad: number): boolean {
    for (const m of this.mouths) {
      const rx = x - m.x, rz = z - m.z;
      const along = rx * m.dx + rz * m.dz, across = -rx * m.dz + rz * m.dx;
      if (along > -pad && along < m.length + pad && Math.abs(across) < m.halfWidth + pad && top > m.floor - 0.3) return true;
    }
    for (const p of this.pieces) {
      if (x < p.box[0] - pad || z < p.box[1] - pad || x > p.box[2] + pad || z > p.box[3] + pad) continue;
      if (top <= p.bottom - 0.3 || bottom >= p.top + 0.3) continue;
      if (nearPolygon(p.outline, x, z, pad)) return true;
    }
    return false;
  }

  /** Whether a footprint, from `bottom` to `top`, overlaps a tube or a portal's mouth. */
  overlaps(outline: Array<[number, number]>, bottom: number, top: number): boolean {
    const xs = outline.map(p => p[0]), zs = outline.map(p => p[1]);
    const box = [Math.min(...xs), Math.min(...zs), Math.max(...xs), Math.max(...zs)];
    for (const m of this.mouths) {
      if (top <= m.floor - 0.3) continue;
      const at = (along: number, across: number): [number, number] => [m.x + m.dx * along - m.dz * across, m.z + m.dz * along + m.dx * across];
      if (overlaps(outline, [at(0, -m.halfWidth), at(m.length, -m.halfWidth), at(m.length, m.halfWidth), at(0, m.halfWidth)])) return true;
    }
    for (const p of this.pieces) {
      if (box[0] > p.box[2] || box[2] < p.box[0] || box[1] > p.box[3] || box[3] < p.box[1]) continue;
      if (top <= p.bottom || bottom >= p.top) continue;
      if (overlaps(outline, p.outline)) return true;
    }
    return false;
  }
}

/** Whether two outlines overlap: a corner of either in the other, or an edge across one. */
function overlaps(a: Array<[number, number]>, b: Array<[number, number]>): boolean {
  if (a.some(([x, z]) => nearPolygon(b, x, z, 0)) || b.some(([x, z]) => nearPolygon(a, x, z, 0))) return true;
  for (let i = 0; i < a.length; i++) {
    const [ax, az] = a[i], [bx, bz] = a[(i + 1) % a.length];
    for (let j = 0; j < b.length; j++) {
      const [cx, cz] = b[j], [dx, dz] = b[(j + 1) % b.length];
      const den = (bx - ax) * (dz - cz) - (bz - az) * (dx - cx);
      if (Math.abs(den) < 1e-12) continue;
      const t = ((cx - ax) * (dz - cz) - (cz - az) * (dx - cx)) / den, u = ((cx - ax) * (bz - az) - (cz - az) * (bx - ax)) / den;
      if (t >= 0 && t <= 1 && u >= 0 && u <= 1) return true;
    }
  }
  return false;
}

/** Whether (x, z) is in a convex polygon, or within `pad` of its edge. */
function nearPolygon(poly: Array<[number, number]>, x: number, z: number, pad: number): boolean {
  let sign = 0, inside = true;
  for (let i = 0; i < poly.length; i++) {
    const [x0, z0] = poly[i], [x1, z1] = poly[(i + 1) % poly.length];
    const c = (x1 - x0) * (z - z0) - (z1 - z0) * (x - x0);
    if (Math.abs(c) < 1e-9) continue;
    if (sign === 0) sign = Math.sign(c);
    else if (Math.sign(c) !== sign) { inside = false; break; }
  }
  if (inside) return true;
  for (let i = 0; i < poly.length; i++) {
    const [x0, z0] = poly[i], [x1, z1] = poly[(i + 1) % poly.length];
    const ex = x1 - x0, ez = z1 - z0, len2 = ex * ex + ez * ez;
    const t = len2 > 0 ? Math.max(0, Math.min(1, ((x - x0) * ex + (z - z0) * ez) / len2)) : 0;
    if (Math.hypot(x - x0 - ex * t, z - z0 - ez * t) < pad) return true;
  }
  return false;
}

/** Darker concrete for a trough's floor, lit. */
const TROUGH_FLOOR = [0.11, 0.105, 0.1];
/** How far inside a trough's open ends its ground is first cut away, metres. */
const TROUGH_LIP = 0.4;
/** How far past the tile's edge the ground of a trough cut there is not drawn, metres. */
const TROUGH_PAST_EDGE = 3;
/** Length of the straight pieces a trough's open ground is cut in, metres. */
const TROUGH_PIECE = 20;
/** Furthest a trough runs on past its underpass, along the way, metres; and the bank that keeps it going, metres over its floor. */
const TROUGH_ON = 40;
const TROUGH_BANK = 1.5;
/** How far from a trough's open ends the ground under it is left alone, metres: a terrain cell and more. */
const TROUGH_END = 9;
/** How far the backfill behind a trough's wall runs level before it slopes away, metres. */
const BACKFILL_FLAT = 2;
/** How far behind a trough's wall the bank it holds is looked at, metres. */
const TROUGH_BACK = 10;
/** How far under its floor a trough's "ceiling" is put, metres: it has no tube to cut the terrain in. */
const TROUGH_NO_TUBE = 10;
/** A trough's wall's top under the road or deck over it, metres: a deck's depth and more. */
const UNDER_DECK = 1.0;
/** Pieces a trough's roof is laid in along each section, to start and end under a road's edge. */
const ROOF_STEPS = 10;
/** Linear RGB: a roof's underside, in shade. */
const ROOF = CONCRETE.map(c => c * 0.72);
/** Most a trough's wall stands over its floor, metres. */
const TROUGH_TOP = 9;

export interface Troughs {
  /** The walls, their copings and the floor between, lit, drawn with the bridge structure. */
  mesh: { positions: Float32Array; colors: Float32Array; indices: Uint32Array };
  /** Solid, as `TunnelMeshes.walls`. */
  walls: Float32Array;
  /** Where the terrain is not drawn, open over: as a portal's mouth, the trough's whole length. */
  boxes: Portal[];
  /** Behind each stretch of wall, the ground to fill up to its top: x0, z0, top0, x1, z1, top1, out x, out z, and how far in under the trough. */
  backs: number[];
  /** The floor between the walls, to stand on: x, y, z triples and indices. */
  floor: { positions: Float32Array; indices: Uint32Array };
}

/**
 * The concrete trough a way runs through under an underpass: a wall either
 * side, from under its floor up to the ground beside it, and a floor slab
 * between. The road over it is bridged (`tunnels.ts`); without walls, the
 * banks it climbs on came down into the way under it as flaps of grass,
 * the ground cut away on one side, filled up on the other. The terrain
 * between the walls is not drawn.
 */
export function buildTroughs(
  positions: Float32Array, indices: Uint32Array, attributes: Float32Array, underpasses: Uint8Array | undefined,
  ground: (x: number, z: number) => number,
  /** The road or deck over (x, z), its surface, if there is one higher than `above`. */
  roadOver: (x: number, z: number, above: number) => number | undefined = () => undefined,
  /** The tile's size, metres: a trough cut at its edge runs on in the neighbour's. */
  tile: { width: number; depth: number } = { width: Infinity, depth: Infinity },
): Troughs {
  const mesh: Mesh = { positions: [], colors: [], indices: [] };
  const walls: number[] = [];
  const boxes: Portal[] = [];
  const backs: number[] = [];
  const floor = { positions: [] as number[], indices: [] as number[] };
  const none = { positions: new Float32Array(0), indices: new Uint32Array(0) };
  if (underpasses === undefined) return { mesh: { ...none, colors: new Float32Array(0) }, walls: new Float32Array(0), boxes, backs, floor: none };
  const quad = (a: number[], b: number[], c: number[], d: number[], col: number[]) => {
    const base = mesh.positions.length / 3;
    mesh.positions.push(...a, ...b, ...c, ...d);
    for (let k = 0; k < 4; k++) mesh.colors.push(col[0], col[1], col[2]);
    mesh.indices.push(base, base + 1, base + 2, base, base + 2, base + 3);
  };
  // Every run of quads, by where it ends, to carry a trough on along the way
  // past the underpass while the banks either side stay high.
  const all = flaggedRuns(indices, new Uint8Array(underpasses.length).fill(1));
  const mid = (a: number) => [(positions[a * 3] + positions[(a + 1) * 3]) / 2, (positions[a * 3 + 2] + positions[(a + 1) * 3 + 2]) / 2];
  // And whether that way is drawn the other way round, its left this one's
  // right: its sections are then read across from the right. Read as they
  // were, the walls changed sides at the portal, crossing in an X.
  const onward = (run: number[], end: number): { vertices: number[]; flipped: boolean } => {
    const [x, z] = mid(run[end === 0 ? 0 : run.length - 1]);
    for (const other of all) {
      if (other === run || other.some(v => underpasses[v] === 1)) continue;
      for (const k of [0, other.length - 1]) {
        const [ox, oz] = mid(other[k]);
        if (Math.hypot(ox - x, oz - z) > 1.5) continue;
        const along = k === 0 ? other.slice(1) : other.slice(0, -1).reverse();
        const out: number[] = [];
        let travelled = 0, [px, pz] = [ox, oz];
        for (const v of along) {
          const [vx, vz] = mid(v);
          travelled += Math.hypot(vx - px, vz - pz);
          [px, pz] = [vx, vz];
          if (travelled > TROUGH_ON) break;
          out.push(v);
        }
        return { vertices: out, flipped: (k === 0) === (end === 0) };
      }
    }
    return { vertices: [], flipped: false };
  };
  for (const run of flaggedRuns(indices, underpasses)) {
    const style = Math.round(attributes[run[0] * 4 + 2]);
    const walk = style === ROAD_STYLE.rail ? WALKWAY + 0.3 : style > ROAD_STYLE.service ? 0.5 : WALKWAY;
    const section = (a: number, flipped = false) => {
      const li = flipped ? a + 1 : a, ri = flipped ? a : a + 1;
      const lx = positions[li * 3], lz = positions[li * 3 + 2], rx = positions[ri * 3], rz = positions[ri * 3 + 2];
      const nx = rx - lx, nz = rz - lz, len = Math.hypot(nx, nz) || 1;
      const y = Math.min(positions[a * 3 + 1], positions[(a + 1) * 3 + 1]) - 0.03;
      const l = [lx - nx / len * walk, lz - nz / len * walk], r = [rx + nx / len * walk, rz + nz / len * walk];
      // Up past the bank behind it: the ground out to a couple of the
      // terrain's cells beyond, which comes down to the wall's foot in one
      // long facet. Only as high as the ground at the wall itself, that facet
      // stood over the wall as a flap of grass.
      // And never up through the road or the deck over it: under a deck, up
      // to its underside; under a road on its bank, buried under it.
      const top = (p: number[], ox: number, oz: number) => {
        let h = ground(p[0], p[1]);
        for (let d = 2; d <= TROUGH_BACK; d += 2) h = Math.max(h, ground(p[0] + ox * d, p[1] + oz * d));
        const t = Math.min(y + TROUGH_TOP, Math.max(y + 1, h + 0.3));
        // The road over it here, or just along: past a road's kerb, a short
        // stretch of wall stood at the road's own level, in the way.
        let over: number | undefined;
        for (const k of [0, -2, 2, -4, 4]) {
          const o = roadOver(p[0] - oz * k, p[1] + ox * k, y + 2);
          if (o !== undefined && (over === undefined || o < over)) over = o;
        }
        return over === undefined ? t : Math.max(y + 1, Math.min(t, over - UNDER_DECK));
      };
      return { l, r, y, lt: top(l, -nx / len, -nz / len), rt: top(r, nx / len, nz / len) };
    };
    // On past each end while there is a bank to hold, either side.
    const banked = (sec: ReturnType<typeof section>) => Math.max(sec.lt, sec.rt) - sec.y > TROUGH_BANK;
    const before: ReturnType<typeof section>[] = [], after: ReturnType<typeof section>[] = [];
    const on0 = onward(run, 0), on1 = onward(run, 1);
    for (const v of on0.vertices) { const sec = section(v, on0.flipped); if (!banked(sec)) break; before.unshift(sec); }
    for (const v of on1.vertices) { const sec = section(v, on1.flipped); if (!banked(sec)) break; after.push(sec); }
    const raw = [...before, ...run.map(v => section(v)), ...after];
    // The tops smoothed along, never under the bank's own height there.
    const sections = raw.map((sec, k) => ({
      ...sec,
      lt: Math.max(sec.lt, ((raw[k - 1]?.lt ?? sec.lt) + 2 * sec.lt + (raw[k + 1]?.lt ?? sec.lt)) / 4),
      rt: Math.max(sec.rt, ((raw[k - 1]?.rt ?? sec.rt) + 2 * sec.rt + (raw[k + 1]?.rt ?? sec.rt)) / 4),
    }));
    // How far each section is from the trough's open ends.
    const fromEnd: number[] = [];
    {
      const c = (k: number) => [(sections[k].l[0] + sections[k].r[0]) / 2, (sections[k].l[1] + sections[k].r[1]) / 2];
      const along = [0];
      for (let k = 1; k < sections.length; k++) along.push(along[k - 1] + Math.hypot(c(k)[0] - c(k - 1)[0], c(k)[1] - c(k - 1)[1]));
      const total = along[along.length - 1];
      for (const a of along) fromEnd.push(Math.min(a, total - a));
    }
    for (let i = 0; i + 1 < sections.length; i++) {
      const a = sections[i], b = sections[i + 1];
      // The walls, faces in, and their copings.
      quad([a.l[0], a.y - 0.6, a.l[1]], [b.l[0], b.y - 0.6, b.l[1]], [b.l[0], b.lt, b.l[1]], [a.l[0], a.lt, a.l[1]], CONCRETE);
      quad([b.r[0], b.y - 0.6, b.r[1]], [a.r[0], a.y - 0.6, a.r[1]], [a.r[0], a.rt, a.r[1]], [b.r[0], b.rt, b.r[1]], CONCRETE);
      const out = (p: number[], q: number[], k: number) => [p[0] + (p[0] - q[0]) / Math.max(1e-6, Math.hypot(p[0] - q[0], p[1] - q[1])) * k, p[1] + (p[1] - q[1]) / Math.max(1e-6, Math.hypot(p[0] - q[0], p[1] - q[1])) * k];
      const al = out(a.l, a.r, 0.4), bl = out(b.l, b.r, 0.4), ar = out(a.r, a.l, 0.4), br = out(b.r, b.l, 0.4);
      quad([a.l[0], a.lt, a.l[1]], [b.l[0], b.lt, b.l[1]], [bl[0], b.lt, bl[1]], [al[0], a.lt, al[1]], CONCRETE);
      quad([ar[0], a.rt, ar[1]], [br[0], b.rt, br[1]], [b.r[0], b.rt, b.r[1]], [a.r[0], a.rt, a.r[1]], CONCRETE);
      // The walls' backs, down into the ground behind them, so a wall seen
      // from behind or past its end is a wall, not its inside through nothing.
      quad([al[0], a.y - 0.6, al[1]], [al[0], a.lt, al[1]], [bl[0], b.lt, bl[1]], [bl[0], b.y - 0.6, bl[1]], CONCRETE);
      quad([br[0], b.y - 0.6, br[1]], [br[0], b.rt, br[1]], [ar[0], a.rt, ar[1]], [ar[0], a.y - 0.6, ar[1]], CONCRETE);
      // The floor between, under the way, and to stand on.
      quad([a.l[0], a.y, a.l[1]], [a.r[0], a.y, a.r[1]], [b.r[0], b.y, b.r[1]], [b.l[0], b.y, b.l[1]], TROUGH_FLOOR);
      const fb = floor.positions.length / 3;
      floor.positions.push(a.l[0], a.y, a.l[1], a.r[0], a.y, a.r[1], b.l[0], b.y, b.l[1], b.r[0], b.y, b.r[1]);
      floor.indices.push(fb, fb + 2, fb + 1, fb + 1, fb + 2, fb + 3);
      // Each wall's end closed, and the floor's edge hung down into the
      // ground there, so no sliver of sky shows under it.
      for (const [sec, at] of [[a, i === 0], [b, i + 2 === sections.length]] as const) {
        if (!at) continue;
        quad([sec.l[0], sec.y - 1.5, sec.l[1]], [sec.r[0], sec.y - 1.5, sec.r[1]], [sec.r[0], sec.y, sec.r[1]], [sec.l[0], sec.y, sec.l[1]], TROUGH_FLOOR);
        const ol = out(sec.l, sec.r, 0.4), or = out(sec.r, sec.l, 0.4);
        quad([sec.l[0], sec.y - 0.6, sec.l[1]], [ol[0], sec.y - 0.6, ol[1]], [ol[0], sec.lt, ol[1]], [sec.l[0], sec.lt, sec.l[1]], CONCRETE);
        quad([or[0], sec.y - 0.6, or[1]], [sec.r[0], sec.y - 0.6, sec.r[1]], [sec.r[0], sec.rt, sec.r[1]], [or[0], sec.rt, or[1]], CONCRETE);
      }
      walls.push(a.l[0], a.l[1], b.l[0], b.l[1], a.y - 0.6, Math.max(a.lt, b.lt));
      walls.push(a.r[0], a.r[1], b.r[0], b.r[1], a.y - 0.6, Math.max(a.rt, b.rt));
      const ox = a.l[0] - a.r[0], oz = a.l[1] - a.r[1], ol = Math.hypot(ox, oz) || 1;
      // How far in under the trough the ground is raised too: across it,
      // away from its open ends, where the way runs on into its cutting.
      const inside = Math.min(fromEnd[i], fromEnd[i + 1]) > TROUGH_END ? ol : 0;
      // Not behind the trough's end stretches: filled up there, the ground
      // past the wall's end stood as a cliff over the track, in rock's grey.
      if (i > 0 && i + 2 < sections.length) {
        backs.push(a.l[0], a.l[1], a.lt, b.l[0], b.l[1], b.lt, ox / ol, oz / ol, inside);
        backs.push(a.r[0], a.r[1], a.rt, b.r[0], b.r[1], b.rt, -ox / ol, -oz / ol, inside);
      }
    }
    // The roof under each road over the trough, which runs on over it
    // unbroken (`tunnels.ts`): its underside over the way, a deck's depth
    // under the road, and a face up to the road where it starts and ends.
    // Both sides of each, seen from in the trough or past its end.
    const both = (p: number[], q: number[], r: number[], t: number[], col: number[]) => { quad(p, q, r, t, col); quad(t, r, q, p, col); };
    let prev: { l: number[]; r: number[]; h: number; top: number } | undefined;
    for (let i = 0; i + 1 < sections.length; i++) {
      const a = sections[i], b = sections[i + 1];
      for (let j = i === 0 ? 0 : 1; j <= ROOF_STEPS; j++) {
        const t = j / ROOF_STEPS;
        const l = [a.l[0] + (b.l[0] - a.l[0]) * t, a.l[1] + (b.l[1] - a.l[1]) * t];
        const r = [a.r[0] + (b.r[0] - a.r[0]) * t, a.r[1] + (b.r[1] - a.r[1]) * t];
        const y = a.y + (b.y - a.y) * t;
        const over = roadOver((l[0] + r[0]) / 2, (l[1] + r[1]) / 2, y + 2);
        const cur = over === undefined ? undefined : { l, r, h: over - UNDER_DECK, top: over - 0.05 };
        if (cur !== undefined && prev !== undefined) {
          both([prev.l[0], prev.h, prev.l[1]], [prev.r[0], prev.h, prev.r[1]], [cur.r[0], cur.h, cur.r[1]], [cur.l[0], cur.h, cur.l[1]], ROOF);
        }
        const face = cur !== undefined && prev === undefined ? cur : cur === undefined && prev !== undefined ? prev : undefined;
        if (face !== undefined) {
          both([face.l[0], face.h, face.l[1]], [face.r[0], face.h, face.r[1]], [face.r[0], face.top, face.r[1]], [face.l[0], face.top, face.l[1]], CONCRETE);
        }
        prev = cur;
      }
    }
    // Where the terrain is not drawn: the trough in straight pieces of
    // about `TROUGH_PIECE`, each wide enough to take its walls in however
    // it bends there, and no wider, or the ground outside them went too.
    for (let from = 0; from + 1 < sections.length;) {
      let to = from + 1, run = 0;
      const centre = (k: number) => [(sections[k].l[0] + sections[k].r[0]) / 2, (sections[k].l[1] + sections[k].r[1]) / 2];
      while (to + 1 < sections.length && run < TROUGH_PIECE) {
        const [ax, az] = centre(to), [bx, bz] = centre(to + 1);
        run += Math.hypot(bx - ax, bz - az);
        to++;
      }
      let [x0, z0] = centre(from);
      const [x1, z1] = centre(to);
      let length = Math.hypot(x1 - x0, z1 - z0);
      if (length > 1.5) {
        const dx = (x1 - x0) / length, dz = (z1 - z0) / length;
        // The trough's open ends a little inside its floor's: the ground
        // there overlaps the floor's edge rather than stopping short of it.
        // But at the tile's edge it runs on in the neighbour's: on past the
        // edge, or a strip of the edge's ground, which nothing presses down,
        // stood across the way there, a wall 3 m high.
        const onEdge = (k: number) => {
          const [cx, cz] = centre(k);
          return Math.min(cx, cz, tile.width - cx, tile.depth - cz) < 1;
        };
        if (from === 0) {
          const by = onEdge(0) ? -TROUGH_PAST_EDGE : TROUGH_LIP;
          x0 += dx * by; z0 += dz * by; length -= by;
        }
        if (to === sections.length - 1) length += onEdge(to) ? TROUGH_PAST_EDGE : -TROUGH_LIP;
        let half = 0, floor = Infinity, top = -Infinity;
        for (let k = from; k <= to; k++) {
          for (const p of [sections[k].l, sections[k].r]) half = Math.max(half, Math.abs(-(p[0] - x0) * dz + (p[1] - z0) * dx));
          floor = Math.min(floor, sections[k].y);
          top = Math.max(top, sections[k].lt, sections[k].rt);
        }
        // No tube: its "inside" is under its floor, so nothing behind its
        // ends is cut. At its floor, the ground just under it behind each
        // open end went for 40 m, a hole beside the track.
        boxes.push({ x: x0, z: z0, dx, dz, halfWidth: half - 0.05, length, floor, ceiling: floor - TROUGH_NO_TUBE, top });
      }
      from = to;
    }
  }
  const pack = { positions: new Float32Array(mesh.positions), colors: new Float32Array(mesh.colors), indices: new Uint32Array(mesh.indices) };
  return { mesh: pack, walls: new Float32Array(walls), boxes, backs, floor: { positions: new Float32Array(floor.positions), indices: new Uint32Array(floor.indices) } };
}

/**
 * Fill the ground behind each trough wall up to just under its top, flat
 * for `BACKFILL_FLAT` and then down at 1 in 1.5 to the land: a retaining
 * wall's backfill. The walls are as tall as the bank behind them is, a few
 * metres out; with the ground left lower right behind, they stood out of it
 * as fins. Only raises, never the tile's edge, never inside a trough.
 */
export function backfillTroughs(
  mesh: { gridRes: number; gridHeights: Float32Array; positions: Float32Array; width: number; depth: number },
  troughs: Troughs,
  /** As `buildTroughs`: the fill stays under a road over it. */
  roadOver: (x: number, z: number, above: number) => number | undefined = () => undefined,
): boolean {
  const res = mesh.gridRes, side = res + 1;
  const b = troughs.backs;
  let changed = false;
  for (let i = 0; i + 8 < b.length; i += 9) {
    const [x0, z0, t0, x1, z1, t1, ox, oz, inside] = [b[i], b[i + 1], b[i + 2], b[i + 3], b[i + 4], b[i + 5], b[i + 6], b[i + 7], b[i + 8]];
    const reach = TROUGH_BACK + 4;
    const minX = Math.min(x0, x1, x0 + ox * reach, x1 + ox * reach), maxX = Math.max(x0, x1, x0 + ox * reach, x1 + ox * reach);
    const minZ = Math.min(z0, z1, z0 + oz * reach, z1 + oz * reach), maxZ = Math.max(z0, z1, z0 + oz * reach, z1 + oz * reach);
    const j0 = Math.max(1, Math.floor(minX / mesh.width * res)), j1 = Math.min(res - 1, Math.ceil(maxX / mesh.width * res));
    const i0 = Math.max(1, Math.floor(minZ / mesh.depth * res) - 1), i1 = Math.min(res - 1, Math.ceil(maxZ / mesh.depth * res) + 1);
    const ex = x1 - x0, ez = z1 - z0, len2 = ex * ex + ez * ez;
    if (len2 < 1e-6) continue;
    for (let gi = i0; gi <= i1; gi++) for (let gj = j0; gj <= j1; gj++) {
      const v = gi * side + gj;
      const x = mesh.positions[v * 3], z = mesh.positions[v * 3 + 2];
      const t = ((x - x0) * ex + (z - z0) * ez) / len2;
      if (t < 0 || t > 1) continue;
      const back = (x - x0) * ox + (z - z0) * oz;
      // In under the trough, its terrain not drawn, only so the ground just
      // outside the wall, drawn between the grid's points, is not dragged
      // down into a ditch along its back.
      if (back < -Math.max(0.5, inside / 2) || back > reach) continue;
      const top = t0 + (t1 - t0) * Math.max(0, Math.min(1, t)) - 0.3;
      let target = top - Math.max(0, back - BACKFILL_FLAT) / 1.5;
      if (target <= mesh.gridHeights[v]) continue;
      // Under a road, just under its surface: filled up through it, the bank
      // showed as a line of grass across the motorway.
      const over = roadOver(x, z, mesh.gridHeights[v]);
      if (over !== undefined) target = Math.min(target, over - 0.25);
      if (target > mesh.gridHeights[v]) { mesh.gridHeights[v] = target; changed = true; }
    }
  }
  return changed;
}

/** Left vertex of each cross-section of each run of quads flagged in `flags`. */
function flaggedRuns(indices: Uint32Array, flags: Uint8Array): number[][] {
  const runs: number[][] = [];
  let current: number[] | undefined;
  let previous = -10;
  for (let q = 0; q * 6 < indices.length; q++) {
    const a = indices[q * 6];
    if (flags[a] !== 1) { current = undefined; continue; }
    if (current === undefined || a !== previous + 2) { current = [a]; runs.push(current); }
    current.push(a + 2);
    previous = a;
  }
  return runs;
}

/** Ground over a tunnel's roof where it is buried, metres. */
const BURY = 0.5;
/** Clear of a portal, along the tunnel, before it is buried, and the distance it is eased in over, metres. */
const BURY_CLEAR = 8;
const BURY_EASE = 12;

/**
 * Bury a tunnel wherever its roof stands out of the land away from its
 * portals, as a cut and cover tunnel is filled over: the ground raised over
 * it, `BURY` over its roof, falling away either side at 1 in 1.5. Straight
 * between its portals, at the grade it may take, a tunnel under a dip in the
 * land it cannot get under stood in the open. Only ever raises the ground;
 * the tile's edge vertices are shared with the neighbour and stay. `flat`
 * are the ribbons' x, z pairs, `design` their floor heights.
 */
export function buryTunnels(
  mesh: { gridRes: number; gridHeights: Float32Array; positions: Float32Array; width: number; depth: number },
  flat: Float32Array, indices: Uint32Array, attributes: Float32Array, bridges: Uint8Array, design: Float32Array,
): boolean {
  const res = mesh.gridRes, side = res + 1;
  let changed = false;
  const runs = tunnelRuns(indices, bridges);
  const runsOn = tunnelJoins(runs, (v) => [(flat[v * 2] + flat[(v + 1) * 2]) / 2, (flat[v * 2 + 1] + flat[(v + 1) * 2 + 1]) / 2]);
  const surfaceEnds = surfaceRunEnds(indices, bridges, v => [(flat[v * 2] + flat[(v + 1) * 2]) / 2, (flat[v * 2 + 1] + flat[(v + 1) * 2 + 1]) / 2]);
  for (const [ri, run] of runs.entries()) {
    const style = Math.round(attributes[run[0] * 4 + 2]);
    const roofAbove = tunnelHeadroom(style) + TUNNEL_ROOF + BURY;
    const n = run.length;
    const mid = run.map(a => [(flat[a * 2] + flat[(a + 1) * 2]) / 2, (flat[a * 2 + 1] + flat[(a + 1) * 2 + 1]) / 2]);
    const half = run.map(a => Math.hypot(flat[a * 2] - flat[(a + 1) * 2], flat[a * 2 + 1] - flat[(a + 1) * 2 + 1]) / 2 + WALKWAY);
    const along: number[] = [0];
    for (let k = 1; k < n; k++) along.push(along[k - 1] + Math.hypot(mid[k][0] - mid[k - 1][0], mid[k][1] - mid[k - 1][1]));
    const total = along[n - 1];
    const inTile = (k: number) => mid[k][0] > 0.5 && mid[k][1] > 0.5 && mid[k][0] < mesh.width - 0.5 && mid[k][1] < mesh.depth - 0.5;
    const hasApproach = (k: number) => surfaceEnds.some(([x, z]) => Math.hypot(x - mid[k][0], z - mid[k][1]) < 1.5);
    const portalAt = [inTile(0) && !runsOn.has(ri * 2) && hasApproach(0), inTile(n - 1) && !runsOn.has(ri * 2 + 1) && hasApproach(n - 1)];
    for (let k = 0; k + 1 < n; k++) {
      // Eased in from each portal; a tile's edge is no portal.
      const fromPortal = Math.min(portalAt[0] ? along[k] : Infinity, portalAt[1] ? total - along[k + 1] : Infinity);
      const ease = Math.max(0, Math.min(1, (fromPortal - BURY_CLEAR) / BURY_EASE));
      if (ease <= 0) continue;
      const [ax, az] = mid[k], [bx, bz] = mid[k + 1];
      const ya = design[run[k]] + roofAbove, yb = design[run[k + 1]] + roofAbove;
      const hw = Math.max(half[k], half[k + 1]);
      const reach = hw + 12;
      const j0 = Math.max(1, Math.floor((Math.min(ax, bx) - reach) / mesh.width * res));
      const j1 = Math.min(res - 1, Math.ceil((Math.max(ax, bx) + reach) / mesh.width * res));
      const i0 = Math.max(1, Math.floor((Math.min(az, bz) - reach) / mesh.depth * res) - 1);
      const i1 = Math.min(res - 1, Math.ceil((Math.max(az, bz) + reach) / mesh.depth * res) + 1);
      const ex = bx - ax, ez = bz - az, len2 = ex * ex + ez * ez;
      for (let i = i0; i <= i1; i++) for (let j = j0; j <= j1; j++) {
        const v = i * side + j;
        const x = mesh.positions[v * 3], z = mesh.positions[v * 3 + 2];
        const t = len2 > 0 ? Math.max(0, Math.min(1, ((x - ax) * ex + (z - az) * ez) / len2)) : 0;
        const d = Math.hypot(x - ax - ex * t, z - az - ez * t);
        const top = ya + (yb - ya) * t - Math.max(0, d - hw) / 1.5;
        const was = mesh.gridHeights[v];
        const target = was + (top - was) * ease;
        if (target > was) { mesh.gridHeights[v] = target; changed = true; }
      }
    }
  }
  return changed;
}

/**
 * The ends of tunnel runs that another tunnel run goes on from, as run × 2,
 * + 1 for the far end: a tunnel the map split into ways, or one branching,
 * which has no portal there.
 */
function surfaceRunEnds(indices: Uint32Array, bridges: Uint8Array, at: (v: number) => number[]): number[][] {
  const ends: number[][] = [];
  for (let q = 0; q < indices.length; q += 6) {
    const a = indices[q];
    if (bridges[a] === TUNNEL) continue;
    if (q === 0 || indices[q - 6] + 2 !== a || bridges[indices[q - 6]] === TUNNEL) ends.push(at(a));
    if (q + 6 >= indices.length || indices[q + 6] !== a + 2 || bridges[indices[q + 6]] === TUNNEL) ends.push(at(a + 2));
  }
  return ends;
}

function tunnelJoins(runs: number[][], at: (v: number) => number[]): Set<number> {
  const ends = runs.flatMap((run, ri) => [[ri * 2, ...at(run[0])], [ri * 2 + 1, ...at(run[run.length - 1])]]);
  const joined = new Set<number>();
  for (const [key, x, z] of ends) {
    for (const [other, ox, oz] of ends) {
      if (other >> 1 === key >> 1) continue;
      if (Math.hypot(ox - x, oz - z) < 1) { joined.add(key); break; }
    }
    // Or one ending on the side of another, a branch.
    if (!joined.has(key)) {
      for (const [ri, run] of runs.entries()) {
        if (ri === key >> 1) continue;
        // Nearby parallel tracks are separate bores, not a continuation.
        // Six metres swallowed their actual entrances in city rail yards.
        if (run.some(v => { const [vx, vz] = at(v); return Math.hypot(vx - x, vz - z) < 1; })) { joined.add(key); break; }
      }
    }
  }
  return joined;
}

/** Left vertex of each cross-section of each run of tunnel quads. */
function tunnelRuns(indices: Uint32Array, bridges: Uint8Array): number[][] {
  const runs: number[][] = [];
  let current: number[] | undefined;
  let previous = -10;
  for (let q = 0; q * 6 < indices.length; q++) {
    const a = indices[q * 6];
    if (bridges[a] !== TUNNEL) { current = undefined; continue; }
    if (current === undefined || a !== previous + 2) { current = [a]; runs.push(current); }
    current.push(a + 2);
    previous = a;
  }
  return runs;
}

/** Whether chunk-local (x, z) is over a portal's mouth or a trough, at any height. */
export function overMouth(p: Portal, x: number, z: number, pad = 0): boolean {
  const rx = x - p.x, rz = z - p.z;
  const along = rx * p.dx + rz * p.dz, across = -rx * p.dz + rz * p.dx;
  return along > -0.05 - pad && along < p.length + pad && Math.abs(across) < p.halfWidth + pad;
}

/** Whether chunk-local (x, z) at height y is in a portal's mouth, where the terrain is not drawn. */
export function inMouth(p: Portal, x: number, y: number, z: number): boolean {
  const rx = x - p.x, rz = z - p.z;
  const along = rx * p.dx + rz * p.dz, across = -rx * p.dz + rz * p.dx;
  return along > -0.05 && along < p.length && Math.abs(across) < p.halfWidth && y > p.floor - 0.3;
}
